import * as React from "react";
import {
  Typography,
  Stack,
  Box,
  Tooltip,
  ListItemButton,
  ListItemText,
  List,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { formatWeightWithUnits } from "../../../utils";
import PrintOutlineICon from "../../../assets/images/Icons/ic_print.svg";
import { PcAndTemplateProductFilterHeader } from "../../../shared/components/PcAndTemplateProductFilterHeader";

export const RecipeListCard = ({
  recipes = [],
  state,
  globalData,
  handleRecipeSelect,
  onDownloadPCByCategoryId,
  downloadProductionChartByCategory,
  toggleAllProducts,
  setToggleAllProducts,
}) => {
  const isCategoryProductList =
    recipes.length > 0 && Array.isArray(recipes[0]?.recipes);

  // Function filter the products
  const filterProducts = (products = [], showInChart) => {
    return products.filter((product) => showInChart || product.show_in_chart);
  };

  return (
    <Stack
      p="8px"
      sx={{
        height: { md: "calc(100vh - 154px)", xs: "100%" },
        overflowY: { md: "auto" },
        width: { md: "347px", xs: "100%" },
        transition: "width 0.5s",
        backgroundColor: "background.default",
      }}
    >
      <PcAndTemplateProductFilterHeader
        checked={toggleAllProducts}
        setChecked={setToggleAllProducts}
        px="0px"
        isCategory={isCategoryProductList}
      />
      {isCategoryProductList ? (
        recipes?.map((item, index) => {
          return (
            <CategoryProductList
              key={index}
              category={item}
              checked={toggleAllProducts}
              globalData={globalData}
              filterProducts={filterProducts}
              onRecipeSelect={handleRecipeSelect}
              onDownload={onDownloadPCByCategoryId}
              printQuery={downloadProductionChartByCategory}
              state={state}
            />
          );
        })
      ) : (
        <DefaultProductList
          state={state}
          data={recipes}
          globalData={globalData}
          onRecipeSelect={handleRecipeSelect}
        />
      )}
    </Stack>
  );
};

const DefaultProductList = ({
  data,
  globalData,
  state,
  onRecipeSelect,
  isCategory = false,
}) => {
  const selectedItem = state?.selectedOtherRecipe;
  const selectedIndex = state?.selectedRecipeIndex;

  return (
    <List
      disablePadding
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: "4px",
      }}
    >
      {data.map((item, index) => {
        const isSelected = isCategory
          ? item.recipe_id === selectedItem?.recipe_id &&
            item.template_id === selectedItem?.template_id
          : index === selectedIndex;

        const lastItem = data.length - 1;
        return (
          <ListItemButton
            key={index}
            onClick={() => onRecipeSelect(index, item)}
            sx={{
              "&.Mui-selected, &.Mui-selected:hover": {
                backgroundColor: "primary.main",
                color: "#ffff",
              },
              backgroundColor: "background.paper",
              px: "8px",
              gap: "8px",
              borderBottom: lastItem === index ? 0 : 1,
              borderColor: "divider",
            }}
            selected={isSelected}
          >
            <ListItemText
              primary={item.recipe_name}
              secondary={
                <Tooltip title={item.template_name} arrow>
                  <Typography
                    fontWeight={500}
                    fontSize="12px"
                    color={isSelected ? "#ffff" : "#8E99AC"}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "150px", // Adjust based on your layout
                      display: "inline-block",
                    }}
                  >
                    • {item.template_name}
                  </Typography>
                </Tooltip>
              }
              primaryTypographyProps={{
                fontSize: "14px",
                fontWeight: 400,
              }}
            />

            {/* Right side: Quantity & arrow */}
            <Box direction="row" gap="8px" alignItems="center" flexShrink={0}>
              <Typography fontWeight={500} fontSize="14px">
                {formatWeightWithUnits(
                  item.total_quantity,
                  item.selling_unit_name,
                  globalData?.all_Units,
                )}
              </Typography>
            </Box>
            <ArrowForwardIosIcon
              sx={{
                fontSize: "16px",
                color: isSelected ? "white" : "black",
              }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );
};

const CategoryProductList = ({
  checked,
  category,
  globalData,
  onDownload,
  printQuery,
  state,
  filterProducts,
  onRecipeSelect,
}) => {
  const isCategoryUnknown = category.id < 1;
  const filteredProducts = filterProducts(category.recipes, checked);
  const loading =
    printQuery.isPending &&
    printQuery.variables.payload.category_id === category.id;

  return filteredProducts.length > 0 ? (
    <React.Fragment>
      <CategoryHeaderSection
        title={isCategoryUnknown ? "" : category.category_name}
        onPrint={isCategoryUnknown ? null : () => onDownload(category.id)}
        loading={loading}
      />

      <DefaultProductList
        isCategory
        state={state}
        data={filteredProducts}
        globalData={globalData}
        onRecipeSelect={onRecipeSelect}
      />
    </React.Fragment>
  ) : (
    <></>
  );
};

const CategoryHeaderSection = ({ title, onPrint = null, loading = false }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      height="36px"
      p="8px"
    >
      <Typography color="gray" fontSize={12} fontWeight={400}>
        {title}
      </Typography>
      {typeof onPrint === "function" ? (
        <IconButton size="small" onClick={onPrint}>
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <Box
              component="img"
              src={PrintOutlineICon}
              alt="print icon"
              sx={{ cursor: "pointer" }}
            />
          )}
        </IconButton>
      ) : (
        <></>
      )}
    </Box>
  );
};
